<script setup lang="ts" generic="T, L extends ZodObjectOrWrapped, K">
import { QueryClient, useMutation, useQueryClient } from '@tanstack/vue-query'
import { toTypedSchema } from '@vee-validate/zod'
import { PencilLine } from 'lucide-vue-next'
import ProtectedButton from 'pejer/src/components/ProtectedButton.vue'
import { Button } from 'pejer/src/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from 'pejer/src/components/ui/dialog'
import { toast } from 'pejer/src/components/ui/toast'
import { ApiUrls } from 'pejer/src/types/base'
import { useForm } from 'vee-validate'
import { computed, ref, unref, watch } from 'vue'
import { z } from 'zod'
import { cn } from '../../lib/utils'
import { ApiResponse, ErrorObject } from '../../types/types'
import DataStateButton from '../DataState/DataStateButton.vue'
import AutoForm from '../ui/auto-form/AutoForm.vue'
import { ZodObjectOrWrapped } from '../ui/auto-form/utils'

const props = defineProps<{
  initialValue: T | null
  fieldConfig?: Record<string, any>
  validationSchema?: L
  displayValue?: string | null
  modalOpen?: boolean
  url?: ApiUrls<any>
  contentClass?: string
  // Emits does not support async functions so we need to pass it in props instead
  submit: (value: T) => Promise<ApiResponse<K>>

  cancelText?: string
  submitText?: string
}>()

const emit = defineEmits<{
  (e: 'onSuccess', queryClient: QueryClient): void
  (e: 'onModalUserClose'): void
}>()

const form = useForm({
  initialValues: props.initialValue,
  validationSchema: props.validationSchema ? toTypedSchema(props.validationSchema) : undefined,
})

const modalOpen = ref(props.modalOpen ?? false)
watch(
  () => props.modalOpen,
  () => {
    modalOpen.value = props.modalOpen ?? false
  }
)

watch(modalOpen, () => {
  reset()
  form.setValues(props.initialValue)
})

const queryClient = useQueryClient()
const { error, isPending, isError, mutate, reset } = useMutation({
  mutationFn: async () => {
    const res = await props.submit(form.values)
    if ('error' in res) {
      throw res
    }
    return res.data
  },
  onError: (e: ErrorObject) => {
    toast({
      title: 'Error',
      description: e.error,
      variant: 'destructive',
    })
  },
  onSuccess: () => {
    emit('onSuccess', queryClient)
    modalOpen.value = false
    toast({
      title: 'Success',
      description: 'Successfully updated',
    })
  },
})

const hasErrors = computed(() => {
  if (!unref(form.errors)) return false
  return Object.keys(unref(form.errors)).length > 0
})

const dataState = computed(() => ({
  isError: isError,
  error: error,
  isLoading: isPending,
}))
</script>
<template>
  {{ displayValue || '' }}
  <Dialog v-model:open="modalOpen" @update:open="$emit('onModalUserClose')">
    <DialogTrigger as-child>
      <slot name="asChild">
        <ProtectedButton
          v-if="displayValue"
          :url="url"
          @click.prevent.stop
          variant="ghost"
          class="p-2 hover:bg-gray-200 rounded-sm group text-gray-600"
        >
          <PencilLine class="group-hover:scale-110" :size="16" />
        </ProtectedButton>
      </slot>
    </DialogTrigger>
    <DialogContent :class="cn('sm:max-w-md', contentClass)">
      <DialogTitle><slot name="title">Edit</slot></DialogTitle>
      <DialogDescription><slot name="description">Edit the value below</slot></DialogDescription>
      <AutoForm
        @submit="() => mutate()"
        :schema="validationSchema ?? z.object({})"
        :form="form"
        :fieldConfig="fieldConfig ?? {}"
      >
        <DialogFooter class="mt-4 flex gap-2">
          <DialogClose as-child>
            <Button :disabled="isPending" type="button" variant="outline">{{
              cancelText ?? 'Cancel'
            }}</Button>
          </DialogClose>
          <DataStateButton :data-state="dataState" type="submit" :disabled="hasErrors">
            <slot name="submit">{{ submitText ?? 'Update' }}</slot>
          </DataStateButton>
        </DialogFooter>
      </AutoForm>
    </DialogContent>
  </Dialog>
</template>
